import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogoMark } from "@/components/brand/Logo";

const SignupPage = () => {
  const { user, signup } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  if (user) return <Navigate to="/app/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setSubmitting(true);
    try {
      await signup({ name, email, password, role: "owner" });
      toast.success("Account created! Welcome to CCS.");
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#FDFBF5]">
      <div className="hidden lg:flex lg:w-[46%] xl:w-[42%] relative overflow-hidden" style={{ background: "#2A1810" }}>
        <div className="absolute inset-0 ccs-grain" />
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 text-[#F0E6D2] w-full">
          <div className="flex items-center gap-3">
            <LogoMark size={52} testid="signup-side-logo" />
            <div>
              <div className="font-display font-semibold text-xl">The CCS Approach</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#F0E6D2]/55">Business Operating System</div>
            </div>
          </div>
          <div className="space-y-5 max-w-md">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">Begin your journey</div>
            <h2 className="font-display text-4xl xl:text-5xl font-semibold leading-[1.1] tracking-tight">
              Built for the work that<br /><span className="text-[#C28A2E]">truly matters.</span>
            </h2>
            <p className="text-[#F0E6D2]/70 leading-relaxed">
              Designed for facilitators and consultants who turn organizations into places people love to work.
            </p>
          </div>
          <div className="text-xs text-[#F0E6D2]/45">© {new Date().getFullYear()} The CCS Approach</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md ccs-fade-up">
          <h1 className="font-display text-4xl font-semibold tracking-tight text-[#2A1810]">Create your workspace</h1>
          <p className="text-[#6B5944] mt-2.5">Start managing your consulting business in minutes.</p>

          <form onSubmit={handleSubmit} className="space-y-5 mt-8" data-testid="signup-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-[#2A1810]">Full name</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Joylance Chapman-George" className="h-12 rounded-xl border-[#EBE3D2] focus-visible:ring-[#5D3A1F] bg-white"
                data-testid="signup-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-[#2A1810]">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@ccsconsulting.com" className="h-12 rounded-xl border-[#EBE3D2] focus-visible:ring-[#5D3A1F] bg-white"
                data-testid="signup-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-[#2A1810]">Password</Label>
              <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters" className="h-12 rounded-xl border-[#EBE3D2] focus-visible:ring-[#5D3A1F] bg-white"
                data-testid="signup-password-input" />
            </div>
            <Button type="submit" disabled={submitting} data-testid="signup-submit-btn"
              className="w-full h-12 rounded-xl bg-[#5D3A1F] hover:bg-[#4A2E18] text-white font-medium text-base transition-all hover:-translate-y-[1px] hover:shadow-lg">
              {submitting ? "Creating workspace…" : "Create my workspace"}
            </Button>
          </form>
          <div className="text-sm text-[#6B5944] mt-7 text-center">
            Already have an account?{" "}
            <Link to="/login" data-testid="link-login" className="text-[#C28A2E] font-semibold hover:underline">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
